import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown, 
  ChevronUp,
  Tag, 
  Package,
  RotateCcw,
  Check
} from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Card, CardContent } from './ui/Card';
import { UsePlaybookFiltersReturn } from '../hooks/usePlaybookFilters';

interface PlaybookFiltersProps {
  filters: UsePlaybookFiltersReturn;
  className?: string;
  compact?: boolean;
}

interface MultiSelectDropdownProps {
  options: string[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder: string;
  icon: React.ReactNode;
  maxDisplayItems?: number;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onSelectionChange,
  placeholder,
  icon,
  maxDisplayItems = 3
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const toggleOption = (option: string) => {
    const newSelection = selectedValues.includes(option)
      ? selectedValues.filter(item => item !== option)
      : [...selectedValues, option];
    onSelectionChange(newSelection);
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  const displayText = selectedValues.length === 0 
    ? placeholder 
    : selectedValues.length <= maxDisplayItems
      ? selectedValues.join(', ')
      : `${selectedValues.slice(0, maxDisplayItems).join(', ')} +${selectedValues.length - maxDisplayItems} more`;

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`
          flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md cursor-pointer
          transition-colors duration-200 min-h-[40px]
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-300 hover:border-gray-400'}
          ${selectedValues.length > 0 ? 'bg-blue-50' : 'bg-white'}
        `}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex-shrink-0 mr-2 text-gray-500">
            {icon}
          </div>
          <span className={`truncate ${selectedValues.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
            {displayText}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          {selectedValues.length > 0 && (
            <button
              onClick={clearSelection}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </div>

      {isOpen && (
        <Card className="absolute z-50 w-full mt-1 shadow-lg">
          <CardContent className="p-0">
            {/* Search input */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder={`Search ${placeholder.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Options list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 text-center">
                  {searchTerm ? 'No matches found' : 'No options available'}
                </div>
              ) : (
                <div className="py-1">
                  {filteredOptions.map((option) => (
                    <div
                      key={option}
                      className={`
                        flex items-center justify-between px-3 py-2 text-sm cursor-pointer
                        transition-colors duration-150
                        ${selectedValues.includes(option) 
                          ? 'bg-blue-50 text-blue-900' 
                          : 'hover:bg-gray-50'
                        }
                      `}
                      onClick={() => toggleOption(option)}
                    >
                      <span className="flex-1 truncate">{option}</span>
                      {selectedValues.includes(option) && (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selection summary */}
            {selectedValues.length > 0 && (
              <div className="p-3 border-t bg-gray-50">
                <div className="text-xs text-gray-600 mb-2">
                  {selectedValues.length} selected
                </div>
                <div className="flex flex-wrap gap-1">
                  {selectedValues.slice(0, 5).map((value) => (
                    <Badge key={value} variant="secondary" className="text-xs">
                      {value}
                    </Badge>
                  ))}
                  {selectedValues.length > 5 && (
                    <Badge variant="outline" className="text-xs">
                      +{selectedValues.length - 5} more
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const PlaybookFilters: React.FC<PlaybookFiltersProps> = ({
  filters,
  className = '',
  compact = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Always start collapsed

  return (
    <Card className={`${className}`}>
      <CardContent className="p-4">
        {/* Collapsed Header - Always Visible */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              {filters.activeFiltersCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {filters.activeFiltersCount} active
                </Badge>
              )}
            </div>
            
            {/* Show active filter preview when collapsed */}
            {!isExpanded && filters.hasActiveFilters && (
              <div className="flex items-center space-x-2 ml-2">
                {filters.filters.search && (
                  <Badge variant="outline" className="text-xs">
                    Search: "{filters.filters.search.length > 20 ? filters.filters.search.substring(0, 20) + '...' : filters.filters.search}"
                  </Badge>
                )}
                {filters.filters.tags.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Tags: {filters.filters.tags.length}
                  </Badge>
                )}
                {filters.filters.productNames.length > 0 && (
                  <Badge variant="outline" className="text-xs">
                    Products: {filters.filters.productNames.length}
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {filters.hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={filters.clearAllFilters}
                className="text-gray-500 hover:text-gray-700"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Hide Filters
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Show Filters
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Expanded Filter Controls */}
        {isExpanded && (
          <div className="mt-6 space-y-6">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Playbooks
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search playbooks by name, description, or workflow..."
                  value={filters.filters.search}
                  onChange={(e) => filters.setSearchFilter(e.target.value)}
                  className="pl-10 pr-4"
                />
                {filters.filters.search && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => filters.setSearchFilter('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Tags Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Tags
              </label>
              <MultiSelectDropdown
                options={filters.filterOptions.tags}
                selectedValues={filters.filters.tags}
                onSelectionChange={filters.setTagsFilter}
                placeholder="Select tags"
                icon={<Tag className="w-4 h-4" />}
              />
            </div>

            {/* Product Names Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Products
              </label>
              <MultiSelectDropdown
                options={filters.filterOptions.productNames}
                selectedValues={filters.filters.productNames}
                onSelectionChange={filters.setProductNamesFilter}
                placeholder="Select products"
                icon={<Package className="w-4 h-4" />}
              />
            </div>

            {/* Active Filters Display - Only show when expanded */}
            {filters.hasActiveFilters && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Active Filters</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={filters.clearAllFilters}
                    className="text-xs text-gray-500 hover:text-gray-700 h-6"
                  >
                    Clear All
                  </Button>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {/* Search filter badge */}
                  {filters.filters.search && (
                    <Badge 
                      variant="outline" 
                      className="flex items-center space-x-1 text-xs"
                    >
                      <Search className="w-3 h-3" />
                      <span>"{filters.filters.search}"</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => filters.setSearchFilter('')}
                        className="h-4 w-4 p-0 ml-1 hover:bg-gray-200"
                      >
                        <X className="w-2 h-2" />
                      </Button>
                    </Badge>
                  )}
                  
                  {/* Tags filter badges */}
                  {filters.filters.tags.map((tag) => (
                    <Badge 
                      key={tag} 
                      variant="outline" 
                      className="flex items-center space-x-1 text-xs"
                    >
                      <Tag className="w-3 h-3" />
                      <span>{tag}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => filters.setTagsFilter(filters.filters.tags.filter(t => t !== tag))}
                        className="h-4 w-4 p-0 ml-1 hover:bg-gray-200"
                      >
                        <X className="w-2 h-2" />
                      </Button>
                    </Badge>
                  ))}
                  
                  {/* Product Names filter badges */}
                  {filters.filters.productNames.map((productName) => (
                    <Badge 
                      key={productName} 
                      variant="outline" 
                      className="flex items-center space-x-1 text-xs"
                    >
                      <Package className="w-3 h-3" />
                      <span>{productName}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => filters.setProductNamesFilter(filters.filters.productNames.filter(p => p !== productName))}
                        className="h-4 w-4 p-0 ml-1 hover:bg-gray-200"
                      >
                        <X className="w-2 h-2" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Results Summary - Only show when expanded */}
            {!filters.loading && (
              <div className="pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Showing {filters.filteredPlaybooks.length} of {filters.playbooks.length} playbooks
                  {filters.isFiltering && (
                    <span className="ml-2 text-blue-600">
                      (filtering...)
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};